import FlexWrap from '@/components/common/FlexWrap';
import { IData } from '@/components/Receipt/RefundReceipt';
import TableRow from '@/components/Receipt/TableRow';
import { useAppSelector } from '@/hooks/useReduxHooks';
import { IGlobalState } from '@/store';
import {
  ICON_CAUTION,
  IMG_DASHED_LINE,
  IMG_LOGO_KTP_BLACK,
  IMG_LOGO_SUCCESSMODE_BLACK,
  IMG_RECEIPT_FLAG,
} from '@repo/assets';
import { Icon } from '@successmode-ktp/kui';
import { CSSProperties, useRef } from 'react';
import { ReactBarcode } from 'react-jsbarcode';
import styled from 'styled-components';

interface IProps {
  info: IData;
  index: number;
  serialNumber?: string;
  style?: CSSProperties;
  onClose?: () => void;
}
function ReceiptImage({ info, index, serialNumber, style, onClose }: IProps) {
  const receiptRef = useRef<HTMLDivElement | null>(null);
  const { nationality } = useAppSelector((state) => state) as IGlobalState;

  return (
    <ImageContainer dir='column' alignItems='center' style={style}>
      <GuideCustomsWrapper>
        {onClose && (
          <CloseButtonWrapper>
            <button type='button' onClick={onClose}>
              <Icon.X fillColor='#fff' />
            </button>
          </CloseButtonWrapper>
        )}
        <GuideCustomsTitle>
          <img src={ICON_CAUTION} alt='' width={18} />
          {nationality === 'KOR'
            ? '세관 직원에게 보여주세요.'
            : 'Show this to Custom officers'}
        </GuideCustomsTitle>
        <GuideCustomDivider />
        <GuideCustomsDescription>
          세관의 승인을 받은 양식의 영수증입니다.
          <br />
          하단의 택스리펀 영수증을 스캔해주세요.
        </GuideCustomsDescription>
        <GuideCustomLabel>
          영수증 스캔이 어렵다면, 고객의 여권을 스캔해주세요.
        </GuideCustomLabel>
      </GuideCustomsWrapper>
      <ReceiptContainer ref={receiptRef} className='notranslate'>
        <ContentBox>
          <LogoContainer gap='20px' alignItems='center'>
            <LogoService src={IMG_LOGO_SUCCESSMODE_BLACK} />
            <LogoService src={IMG_LOGO_KTP_BLACK} />
          </LogoContainer>
          {info?.barcodeS3Path && (
            <Barcode src={info.barcodeS3Path} style={{ top: 152 }} />
          )}
          {serialNumber && (
            <SerialBarcode>
              <SerialBarcodeImage
                value={serialNumber}
                options={{ format: 'CODE39', displayValue: false }}
              />
            </SerialBarcode>
          )}
          <AdditionalInfo isSerialNumber>{info?.purchaseSn}</AdditionalInfo>
          <AdditionalInfo>
            KTP 환급전표
            <br />
            Korea Taxfree Payment receipt fot Tax Refund
            <br />
            https://ktaxpay.com/
            <br />
            (국세청인정서식 Authorized by Korea NTS)
          </AdditionalInfo>
          <RowWrap>
            {info?.rowDataArr.map((item, idx) => (
              <TableRow key={idx} {...item} refundAfter={info.refundAfter} />
            ))}
          </RowWrap>
          {info.refundAfter && (
            <>
              <BlackGuideText>
                Pls present your passport, goods and receipt
              </BlackGuideText>
              <AdditionalInfo isSerialNumber>{info.purchaseSn}</AdditionalInfo>
              {info.barcodeS3Path && <BarcodeSection />}
              <DashedLine src={IMG_DASHED_LINE} />
            </>
          )}
          <GrayText>https://ktaxpay.com </GrayText>
          <FlexWrap justifyContent='space-evenly'>
            <GrayText>석세스모드</GrayText>
            <GrayText>제 138-00078 호</GrayText>
          </FlexWrap>
          <br />
          <GrayText>{`- ${index + 1} - `}</GrayText>
          <br />
          <br />
          {info.barcodeS3Path && (
            <Barcode src={info.barcodeS3Path} style={{ bottom: 324 }} />
          )}
        </ContentBox>
      </ReceiptContainer>
      <ReceiptEdge src={IMG_RECEIPT_FLAG} reverse />
    </ImageContainer>
  );
}

const ImageContainer = styled(FlexWrap)`
  margin: 36px 0 50px;
  overflow: auto;
`;

const CloseButtonWrapper = styled.button`
  display: flex;
  justify-content: flex-end;
  width: 100%;
`;

const ReceiptContainer = styled.div`
  width: 335px;
  -webkit-box-shadow: 0px 0px 12px 1px rgba(0, 0, 0, 0.2);
  box-shadow: 0px 0px 12px 1px rgba(0, 0, 0, 0.2);
  background-color: #fff;
`;
const ReceiptEdge = styled.img<{ reverse?: boolean }>`
  width: 335px;
  transform: ${({ reverse }) => reverse && 'rotate(180deg)'} translate(0, 1px);
`;
const ContentBox = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
`;
const LogoContainer = styled(FlexWrap)`
  margin: 40px 0 24px;
`;
const GuideCustomsWrapper = styled.div`
  width: 335px;
  padding: 16px 14px;
  background: #0b42ad;
`;
const GuideCustomsTitle = styled.strong`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 5px;
  width: 100%;
  color: #fff;
  text-align: center;
  font-size: 18px;
  font-weight: 600;
  line-height: 150%;
`;

const GuideCustomDivider = styled.div`
  margin-block: 12px;
  border-bottom: 1px solid #fff;
`;

const GuideCustomsDescription = styled.p`
  font-size: 16px;
  color: #fff;
  text-align: center;
  line-height: 150%;
`;

const GuideCustomLabel = styled.div`
  display: flex;
  padding: 6px 8px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  align-self: stretch;
  border-radius: 4px;
  background: var(--KTP-Color-Primary-150, #1856cd);
  color: var(--KTP-Primary-10, var(--KTP-Color-Primary-10, #e9f0fe));
  text-align: center;
  margin-top: 12px;

  /* Body/14px */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 150%; /* 21px */
  letter-spacing: -0.28px;
`;

const LogoService = styled.img`
  width: 130px;
`;
const Barcode = styled.img`
  margin: 17px 0px 29px;
  max-width: 335px;
  height: auto;
`;
const BarcodeSection = styled.div`
  height: 80px;
`;
const AdditionalInfo = styled.p<{ isSerialNumber?: boolean }>`
  text-align: center;
  color: #5f6165;
  line-height: 22px;
  font-size: 14px;
  font-weight: 400;
  margin-bottom: ${(props) => (props.isSerialNumber ? '10px' : '35px')};
`;
const RowWrap = styled.div`
  padding: 0 20px;
`;
const GrayText = styled.p`
  color: #5f6165;
  font-size: 14px;
  line-height: 22px;
  font-weight: 400;
  text-align: center;
`;
const DashedLine = styled.img`
  width: 100%;
  height: 1px;
  padding: 0 20px;
  margin-bottom: 16px;
`;
const BlackGuideText = styled.p`
  font-size: 14px;
  line-height: 22px;
  margin: 100px 0 10px;
  text-align: center;
`;
const SerialBarcode = styled.div`
  width: 100%;
  overflow: hidden;
  margin-top: 20px;
`;

const SerialBarcodeImage = styled(ReactBarcode)`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;
export default ReceiptImage;
